// F18 — the "Report uploaded confirmation" field, converged.
//
// This component existed TWICE, byte-identical apart from its name, one
// line of whitespace and the wording of a code comment: as
// `ReportUploadedField` in <CertificationItemModal> (Section 2) and as
// `UniversalAttestationField` in <CriteriaChecklistItemPopover>
// (Section 1). Two copies of one regulated attestation sentence are two
// copies free to drift, and the drift had already started — both had
// grown the same timezone defect independently, so one finding needed
// fixing in two places.
//
// The whole field converges, not just the timestamp: the label, the
// checkbox, the helper text and the stamp were all duplicated verbatim.
// The two HOSTS stay cloned on purpose — <CertificationItemModal>'s own
// header explains why (different DTO, different form-schema engine,
// different escalation flow) — but nothing in that rationale reached
// this leaf field.
//
// TIMEZONE / LOCALE — the substance of F18. Both copies rendered the
// stamp with `new Intl.DateTimeFormat(undefined, …)`. Passing `undefined`
// as the locale takes BOTH the locale and the timezone from the browser,
// so one attestation read `Sep 3, 2026, 6:00 PM` in Phoenix,
// `Sep 4, 2026, 10:00 AM` in Tokyo — a different calendar DAY — and
// `4 Sept 2026, 01:00` to a reader whose browser is set to en-GB. On a
// record whose entire purpose is to say who attested what and when, the
// answer must not depend on the machine reading it.
//
// `attestedAt` is an INSTANT, not a wall-calendar value: both writers
// assign `DateTimeOffset.UtcNow` at the moment a person ticks the box
// (`CriteriaChecklistService.UpdateJustificationAsync` /
// `UnderwritingCertificationService.ApplyAttestation`), and the columns
// were created as `datetimeoffset` — never widened from a `date`. So it
// renders in the VIEWER's zone via `useDateFormatter`, which pins the
// locale to `en-US` and the zone to the signed-in user's `MeDto.timeZone`
// (falling back to Phoenix). It is deliberately NOT `formatCalendarDate`,
// which ignores the zone and would drop the time of day this record needs.
//
// The helper's defaults are exactly the option set both copies spelled
// out by hand, so no override is passed — a second set of options is a
// second thing to drift.

import { useDateFormatter } from "@/utils/datetime";

export interface ReportUploadedAttestationFieldProps {
  /** LOCAL, unsaved checkbox state owned by the host modal. */
  checked: boolean;
  /** Server state — whether the persisted row carries an attestation. */
  persistedAttested: boolean;
  /** Server-resolved display name. The read projection already collapses
   *  DisplayName → UserName → Email, so this is rendered verbatim; the
   *  local fallback covers only a genuinely null projection. */
  persistedDisplayName: string | null;
  /** UTC instant the attestation was recorded (`DateTimeOffset`). */
  persistedAttestedAt: string | null;
  onChange: (next: boolean) => void;
  disabled: boolean;
}

export function ReportUploadedAttestationField({
  checked,
  persistedAttested,
  persistedDisplayName,
  persistedAttestedAt,
  onChange,
  disabled,
}: ReportUploadedAttestationFieldProps) {
  const { dateTime } = useDateFormatter();

  return (
    <div className="flex flex-col gap-space-1">
      <span className="font-display text-label uppercase tracking-label text-gold-shadow m-0">
        Report uploaded confirmation <span className="text-status-error">*</span>
      </span>
      <label className="inline-flex items-start gap-space-2 text-small text-onyx">
        <input
          type="checkbox"
          className="mt-[3px]"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
        />
        <span>
          I have generated and uploaded the associated report to the Document
          Repository.
        </span>
      </label>
      <p className="text-caption text-gray-mid m-0">
        Required to certify or decline this item. Reports must be uploaded via
        the Documents tab on this deal before this box is checked.
      </p>
      {/* The stamp reads only from server state. It is an audit fact about
          the persisted attestation, not a preview of an unsaved checkbox
          toggle — it appears the next time the modal opens after Save. */}
      {persistedAttested && persistedAttestedAt && (
        <p className="text-caption text-gray-mid italic m-0">
          Attested by {persistedDisplayName ?? "Unknown user"} on{" "}
          {dateTime(persistedAttestedAt)}
        </p>
      )}
    </div>
  );
}
